import { useEffect, useState } from 'react';

import { DEFAULT_FEATURE_TTL_DAYS, FeatureDescriptor, getFeature } from '../config/featureRegistry';

/**
 * localStorage-backed "seen" state for feature-discovery badges.
 *
 * Dismissal is hybrid: a badge disappears once the user interacts with the
 * feature (explicit dismissal stored here) OR once its TTL elapses (implicit,
 * computed from the registry). State is per-feature and survives reloads.
 *
 * Cross-component reactivity is achieved by dispatching a window event on
 * dismissal and also listening for the native `storage` event (other tabs).
 */
const STORAGE_PREFIX = 'pearsana.featureSeen.';
const SEEN_EVENT = 'pearsana:feature-seen';

const storageKey = (id: string) => `${STORAGE_PREFIX}${id}`;

export const isFeatureDismissed = (id: string): boolean => {
	try {
		return window.localStorage.getItem(storageKey(id)) === '1';
	} catch {
		return false;
	}
};

/** Mark a feature as seen and notify all listeners. Idempotent. */
export const dismissFeature = (id: string): void => {
	try {
		if (window.localStorage.getItem(storageKey(id)) === '1') {
			return;
		}
		window.localStorage.setItem(storageKey(id), '1');
	} catch {
		/* localStorage unavailable (private mode / quota) — still notify in-session */
	}
	window.dispatchEvent(new CustomEvent(SEEN_EVENT, { detail: id }));
};

/**
 * When the badge window starts.
 *
 * For a feature that ships on, that is its release date. For one that ships dark
 * behind a flag, it is the moment the flag was switched on — release and
 * availability are different events, and the badge should follow the one users
 * experience. A dark feature with no timestamp has never been switched on, so it
 * has no window at all.
 */
const windowStart = (feature: FeatureDescriptor, enabledAt?: Record<string, string>): number | null => {
	if (feature.flag) {
		const enabled = enabledAt?.[feature.flag];
		if (!enabled) {
			return null;
		}
		const at = new Date(enabled).getTime();
		return Number.isNaN(at) ? null : at;
	}
	const released = new Date(feature.releaseDate).getTime();
	return Number.isNaN(released) ? null : released;
};

const isWithinTtl = (feature: FeatureDescriptor, enabledAt?: Record<string, string>): boolean => {
	const start = windowStart(feature, enabledAt);
	if (start === null) {
		return false;
	}
	const ttlMs = (feature.ttlDays ?? DEFAULT_FEATURE_TTL_DAYS) * 86_400_000;
	return Date.now() < start + ttlMs;
};

/** Pure check: is this feature currently new for the user? */
export const computeIsFeatureNew = (id?: string, enabledAt?: Record<string, string>): boolean => {
	if (!id) {
		return false;
	}
	const feature = getFeature(id);
	if (!feature) {
		return false;
	}
	return isWithinTtl(feature, enabledAt) && !isFeatureDismissed(id);
};

const subscribe = (handler: () => void) => {
	window.addEventListener(SEEN_EVENT, handler);
	window.addEventListener('storage', handler);
	return () => {
		window.removeEventListener(SEEN_EVENT, handler);
		window.removeEventListener('storage', handler);
	};
};

/** Reactively report whether a single feature is new. */
export const useIsFeatureNew = (id?: string, enabledAt?: Record<string, string>): boolean => {
	const [isNew, setIsNew] = useState(() => computeIsFeatureNew(id, enabledAt));
	// The map identity changes on every store read; its content is what matters.
	const enabledKey = JSON.stringify(enabledAt ?? {});

	useEffect(() => {
		const map = JSON.parse(enabledKey) as Record<string, string>;
		setIsNew(computeIsFeatureNew(id, map));
		return subscribe(() => setIsNew(computeIsFeatureNew(id, map)));
	}, [id, enabledKey]);

	return isNew;
};

/** Reactively report whether ANY of the given features is new (for roll-ups). */
export const useAnyFeatureNew = (ids: string[], enabledAt?: Record<string, string>): boolean => {
	const key = ids.join(',');
	const enabledKey = JSON.stringify(enabledAt ?? {});
	const [anyNew, setAnyNew] = useState(() => ids.some((id) => computeIsFeatureNew(id, enabledAt)));

	useEffect(() => {
		const map = JSON.parse(enabledKey) as Record<string, string>;
		const compute = () => key.split(',').filter(Boolean).some((id) => computeIsFeatureNew(id, map));
		setAnyNew(compute());
		return subscribe(() => setAnyNew(compute()));
	}, [key, enabledKey]);

	return anyNew;
};
